/**
 * Dashboard resource service — Dashboard API, dashboard module (CT-5183).
 *
 * The seven write/read endpoints behind the /dashboards screen, under
 * `/v0/dashboard/dashboard`. Same envelope and same camelCase vocabulary as
 * the widget module, and the same rule about identity: `workspaceUuid` and
 * `userUuid` come from the authorizer and are **never** put in a body.
 *
 * Contract source: `dashboard_module_frontend_contract.md` (phase 1).
 *
 * TODO(CT-5183 / backend): these paths answer `404 Not Found` at the gateway
 * today — the module is not deployed yet. `apiDataAdapters` treats that as
 * "not deployed" and falls back to the local seeds so the screen stays usable;
 * nothing else in this file knows or cares.
 */

import { validateApiResponse, normalizePagedMeta } from '../shared/utils/apiResponse.js';
import { apiDashboards, DASHBOARD_API_PREFIX } from './apiClient.js';
import { sectionLabelForKey } from '../shared/constants/sectionFallback.js';
import { mapApiWidgetInstanceToUi, toApiWidgetInstance } from './widgetInstances.js';

/** Lifecycle states, uppercase on the wire and lowercase in `DASHBOARD_STATES`. */
export const DASHBOARD_API_STATES = Object.freeze(['DRAFT', 'PUBLISHED', 'ARCHIVED']);

/** `orderBy` whitelist for `dashboard/list`. */
export const DASHBOARD_ORDER_BY = Object.freeze([
  'updated_at',
  'created_at',
  'name',
  'published_at',
]);

const DEFAULT_PAGE_SIZE = 20;

/**
 * Drop `undefined` / `''` / `[]` so an untouched filter never reaches the wire.
 *
 * Only ever applied to filters and metadata — **never** to a composition. An
 * empty `widgets` list is a statement ("this dashboard has no widgets any
 * more"), and compacting it away would turn removing the last widget into a
 * metadata-only save that silently changes nothing.
 */
const compact = (body) =>
  Object.fromEntries(
    Object.entries(body).filter(([, value]) => {
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }),
  );

/** Strip `null` values so a caller's fallback is not overwritten by one. */
const dropNulls = (value) =>
  Object.fromEntries(Object.entries(value ?? {}).filter(([, v]) => v != null));

/** `DRAFT` ⇄ `draft`. The UI's `DASHBOARD_STATES` are lowercase throughout. */
export const toUiState = (state) => (state ? String(state).toLowerCase() : '');
export const toApiState = (state) => (state ? String(state).toUpperCase() : undefined);

/**
 * `sectionName` is the human path ("Contacts / UCP / Overview") the cards
 * print; when the backend does not resolve it, the key is the next best thing
 * to show, and an empty string is better than the word "undefined".
 */
/*
  The backend's own name wins; the picker's catalog names the key when the read
  did not carry one — `dashboard/get` does not — and the bare key is the last
  resort rather than the usual case it had become (CT-5898).
*/
const toSectionPath = (raw = {}) =>
  raw.sectionName || sectionLabelForKey(raw.sectionKey) || raw.sectionKey || '';

/**
 * List row → the card the Created tab renders.
 *
 * The UI's `sectionId` **is** the contract's `sectionKey`: the key is what
 * every filter and every write takes, so carrying an id the API does not know
 * would mean translating in both directions forever.
 */
export const mapApiDashboardToUi = (raw = {}) => ({
  id: raw.dashboardUuid ?? '',
  title: raw.name ?? '',
  description: raw.description ?? '',
  icon: raw.icon ?? null,
  state: toUiState(raw.state ?? raw.versionState) || 'draft',
  sectionId: raw.sectionKey ?? '',
  sectionPath: toSectionPath(raw),
  dashboardType: raw.dashboardType ?? '',
  hasDraft: raw.hasDraft ?? false,
  publishedVersionNumber: raw.publishedVersionNumber ?? null,
  widgetCount: raw.widgetCount ?? 0,
  // The card prints category names as its tag row; the contract's categories
  // are `{categoryKey, name}` pairs.
  tags: (raw.categories ?? []).map((category) => category.name ?? category.categoryKey ?? ''),
  categoryKeys: (raw.categories ?? []).map((category) => category.categoryKey ?? ''),
  creatorName: raw.owner?.name ?? '',
  ownerUuid: raw.owner?.userUuid ?? '',
  isDefault: raw.isDefault ?? false,
  updatedAt: raw.updatedAt ?? null,
  // The list contract has no `createdAt`, and the list page sorts its
  // non-archived cards by it — left null every card ties at epoch 0 and the
  // order is whatever the array happened to be. `updatedAt` is the closest
  // true thing the row carries.
  createdAt: raw.createdAt ?? raw.updatedAt ?? null,
  // Sharing is not in the phase-1 contract (see `apiDataAdapters`).
  sharedWith: [],
});

/**
 * `dashboard/get` → the full editor state: the row above plus the composition
 * and the version pointers the builder needs.
 */
export const mapApiDashboardDetailToUi = (raw = {}) => ({
  ...mapApiDashboardToUi(raw),
  state: toUiState(raw.versionState ?? raw.state) || 'draft',
  dashboardStatus: raw.dashboardStatus ?? '',
  isTemplate: raw.isTemplate ?? false,
  allowUserReposition: raw.allowUserReposition ?? false,
  allowUserResize: raw.allowUserResize ?? false,
  displayOrder: raw.displayOrder ?? 0,
  currentDraft: raw.currentDraft ?? null,
  currentPublished: raw.currentPublished ?? null,
  sourceTemplate: raw.sourceTemplate ?? null,
  // The token the next save has to echo back as `expectedRevision`; without it
  // a save is unconditional and two tabs overwrite each other silently.
  revision: raw.currentDraft?.revision ?? null,
  sections: raw.sections ?? [],
  filters: raw.filters ?? [],
  widgets: (raw.widgets ?? []).map(mapApiWidgetInstanceToUi),
  widgetCount: (raw.widgets ?? []).length || (raw.widgetCount ?? 0),
});

/**
 * `create` / `update` / `duplicate` return an operation result, not a
 * dashboard: the uuid, the new draft pointer and the `clientRef → uuid` pairs.
 */
export const mapApiDashboardWriteResult = (raw = {}) => ({
  id: raw.dashboardUuid ?? '',
  draftVersionNumber: raw.draftVersionNumber ?? raw.currentDraft?.versionNumber ?? null,
  revision: raw.revision ?? raw.currentDraft?.revision ?? null,
  currentDraft: raw.currentDraft ?? null,
  updatedAt: raw.currentDraft?.updatedAt ?? null,
  instanceIds: (raw.widgets ?? []).map((pair) => ({
    clientRef: pair.clientRef ?? '',
    instanceUuid: pair.instanceUuid ?? '',
  })),
  sourceTemplate: raw.sourceTemplate ?? null,
  previousDefaultDashboardUuid: raw.previousDefaultDashboardUuid ?? null,
});

/**
 * The sections a composition has to declare for its own placements.
 *
 * Every instance carries a `sectionCode`, and the backend resolves it against
 * the sections **in the same composition** — an undeclared one comes back as
 * `DSW024 Section <code> not found`, which is what every save with a widget
 * used to hit: the builder has no section UI, so it put every placement in
 * `main` and declared nothing.
 *
 * Derived from the placements rather than invented, so the day the builder does
 * expose sections this keeps working, and a caller that passes its own
 * `sections` wins.
 */
const sectionsFor = (instances) => {
  const codes = [...new Set(instances.map((i) => i.sectionCode).filter(Boolean))];
  return codes.map((sectionCode, index) => ({
    sectionCode,
    label: sectionCode,
    displayOrder: index + 1,
  }));
};

/**
 * Editor state → the composition block `create` and `update` share.
 * Returned as `null` when there is nothing to send, because the contract makes
 * the composition optional and omitting it is how a metadata-only save is
 * expressed.
 */
const toComposition = (payload = {}) => {
  if (!Array.isArray(payload.widgets)) return null;
  const widgets = payload.widgets.map(toApiWidgetInstance);
  return {
    sections: payload.sections?.length ? payload.sections : sectionsFor(widgets),
    filters: payload.filters ?? [],
    widgets,
  };
};

/**
 * One page of dashboards in one section — the Created tab's only read.
 *
 * **Keyset, and scoped to a section.** `sectionKey` is required, `cursor` is an
 * opaque dashboard uuid (pass `meta.nextCursor` back) and `pageSize` caps the
 * page at 100. The scope-keyset read applies no other server-side filter or
 * ordering this phase, and the backend *rejects* the offset/filter inputs
 * rather than ignoring them — `page`, `orderBy`, `orderDirection`, `state`,
 * `dashboardType`, `categoryKeys` and `searchKeyword` all come back `DSW001`.
 * Sending any of them is how this endpoint used to fail on every call.
 *
 * Searching and filtering by state therefore happen above this, in
 * `apiDataAdapters`, over the merged rows.
 *
 * @param {{ sectionId: string, pageSize?: number, cursor?: string }} params
 */
export async function listDashboards({ sectionId, pageSize, cursor } = {}) {
  const response = await apiDashboards.post(
    `${DASHBOARD_API_PREFIX}/list`,
    compact({
      sectionKey: sectionId,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
      cursor,
    }),
  );

  const { body, meta } = validateApiResponse(response, 'Could not load dashboards.');
  const rows = body ?? [];
  return {
    dashboards: rows.map(mapApiDashboardToUi),
    // `totalItems` comes back `null` on a page that plainly has rows, and a
    // consumer that paginates would then read zero and hide them. Falling back
    // to what this page actually returned is never worse than claiming none.
    meta: normalizePagedMeta({ totalItems: rows.length, ...dropNulls(meta) }),
  };
}

/** Read one dashboard with its effective composition (draft, else published). */
export async function getDashboard(dashboardId) {
  const response = await apiDashboards.get(
    `${DASHBOARD_API_PREFIX}/${encodeURIComponent(dashboardId)}`,
  );
  const { body } = validateApiResponse(response, 'Could not load the dashboard.');
  return mapApiDashboardDetailToUi(body ?? {});
}

/**
 * Create a dashboard — metadata only.
 *
 * **No composition.** `create` accepts a `widgets` list in its schema but
 * rejects a non-empty one, in its own words:
 *
 * > `DSW001` — Initial composition on create is not supported yet; create the
 * > dashboard, then update it with the composition.
 *
 * So the widgets go to `update` straight after, which `apiDataAdapters`
 * orchestrates — a create that silently dropped them would be worse than the
 * error. (Verified against dev on 2026-09-25: an empty `widgets: []` is
 * accepted and pointless, a populated one is refused.)
 *
 * `fromTemplate` is still sent when a template was chosen, and is still
 * refused — `DSW001` "Create from template is not available in this phase."
 * Dropping it would turn a template copy into a silently empty dashboard,
 * which is the one outcome worse than a clear error, and it starts working the
 * day the backend enables it.
 *
 * `sectionKey` is immutable afterwards, which is why it is required here and
 * absent from `updateDashboard`.
 */
export async function createDashboard(payload = {}) {
  const response = await apiDashboards.post(
    `${DASHBOARD_API_PREFIX}/create`,
    compact({
      sectionKey: payload.sectionId,
      name: payload.title,
      description: payload.description,
      icon: payload.icon,
      dashboardType: payload.dashboardType,
      categoryKeys: payload.categoryKeys,
      ...(payload.templateId ? { fromTemplate: { templateUuid: payload.templateId } } : {}),
    }),
  );
  const { body } = validateApiResponse(response, 'Could not create the dashboard.');
  return mapApiDashboardWriteResult(body ?? {});
}

/**
 * Save: metadata (non-versioned) and, optionally, the draft composition
 * (versioned).
 *
 * **The composition is upsert-only, not a replacement.** Each section, filter,
 * widget and layout sent is created or updated; *children left out are kept*.
 * There is no child-removal path this phase, so a widget the author deleted
 * from the canvas survives the save and comes back on the next read — the
 * editor warns about that rather than pretending otherwise
 * (`useDashboardEditor`). Archiving the whole dashboard is the only removal.
 *
 * `expectedRevision` is required by the contract whenever a composition is
 * sent; a mismatch comes back as `DSW021` and the client has to reload.
 */
export async function updateDashboard(dashboardId, payload = {}, { expectedRevision } = {}) {
  const composition = toComposition(payload);
  const response = await apiDashboards.post(`${DASHBOARD_API_PREFIX}/update`, {
    ...compact({
      dashboardUuid: dashboardId,
      name: payload.title,
      description: payload.description,
      icon: payload.icon,
      categoryKeys: payload.categoryKeys,
      allowUserReposition: payload.allowUserReposition,
      allowUserResize: payload.allowUserResize,
      displayOrder: payload.displayOrder,
      isDefault: payload.isDefault,
      dashboardStatus: payload.dashboardStatus,
    }),
    ...(composition
      ? { ...(expectedRevision == null ? {} : { expectedRevision }), ...composition }
      : {}),
  });
  const { body } = validateApiResponse(response, 'Could not save the dashboard.');
  return mapApiDashboardWriteResult(body ?? {});
}

/**
 * The validation outcome, from either shape the endpoint uses.
 *
 * The contract documents `validationStatus` + `validationIssues` at the top of
 * the body, and that is what a successful publish returns. A *rejected* one
 * (`DSW022`) answers with `{ validationReport: { status, issues } }` instead —
 * verified against dev on 2026-09-24 — and the dialog that lists the offending
 * instances has to find them in both.
 */
const readValidation = (body = {}) => {
  const report = body?.validationReport;
  return {
    validationStatus: body?.validationStatus ?? report?.status ?? null,
    validationIssues: body?.validationIssues ?? report?.issues ?? [],
  };
};

/**
 * Publish: the backend validates the draft (section published, at least one
 * instance, pinned widget versions PUBLISHED, a DESKTOP layout per instance)
 * and then snapshots it. A rejection is `DSW022` and carries
 * `validationIssues`, which `validateApiResponse` turns into a throw with the
 * backend's own wording — the issues stay reachable on `error.response.data`.
 */
export async function publishDashboard(dashboardId, { versionNotes } = {}) {
  const response = await apiDashboards.post(
    `${DASHBOARD_API_PREFIX}/publish`,
    compact({ dashboardUuid: dashboardId, versionNotes }),
  );
  const { body } = validateApiResponse(response, 'Could not publish the dashboard.');
  return {
    id: body?.dashboardUuid ?? dashboardId,
    publishedVersionNumber: body?.publishedVersionNumber ?? null,
    publishedAt: body?.publishedAt ?? null,
    snapshotGeneratedAt: body?.snapshotGeneratedAt ?? null,
    hash: body?.hash ?? null,
    ...readValidation(body),
  };
}

/**
 * Archive (the kebab's `Archive`). A soft delete: the dashboard leaves the
 * runtime lists and keeps its versions, and `updateDashboard` with
 * `dashboardStatus: 'ACTIVE'` restores it.
 */
export async function archiveDashboard(dashboardId) {
  const response = await apiDashboards.post(`${DASHBOARD_API_PREFIX}/delete`, {
    dashboardUuid: dashboardId,
  });
  const { body } = validateApiResponse(response, 'Could not archive the dashboard.');
  return {
    id: body?.dashboardUuid ?? dashboardId,
    state: toUiState(body?.dashboardStatus) || 'archived',
  };
}

/** Duplicate: copies the latest draft (or the published version) into a new DRAFT. */
export async function duplicateDashboard(dashboardId, { name } = {}) {
  const response = await apiDashboards.post(
    `${DASHBOARD_API_PREFIX}/duplicate`,
    compact({ dashboardUuid: dashboardId, name }),
  );
  const { body } = validateApiResponse(response, 'Could not duplicate the dashboard.');
  return mapApiDashboardWriteResult(body ?? {});
}
